import { Node, type Command } from '@tiptap/core';
import { liftTarget } from '@tiptap/pm/transform';
import { Decoration, DecorationSet } from 'prosemirror-view';
import { Plugin } from 'prosemirror-state';
import { Node as ProseMirrorNode } from 'prosemirror-model';
import { cloneDeep } from 'lodash-es';
import { BasicBulletList, BasicMultiLevelList } from './list';
import { getActiveParagraphBreakFont } from './WordParagraph';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        numberingList: {
            toggleOrderedList: () => ReturnType;
            toggleBulletList: () => ReturnType;
            splitNumberingList: () => ReturnType;
            sinkNumberingList: () => ReturnType;
            liftNumberingList: () => ReturnType;
            removeNumberingListOnBackspace: () => ReturnType;
            setNumberingData: (numberingPart: { numberings: { numId: string; abstractNumId: string }[]; abstractNumberings: any[] }) => ReturnType;
        };
    }
}

// prettier-ignore
function formatNumber(number: number, format: string): string {
    switch (format) {
        case 'decimal':
            return number.toString();
        case 'upper-alpha': {
            const upperAlpha = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'];
            return upperAlpha[number - 1] || number.toString();
        }
        case 'lower-alpha': {
            const lowerAlpha = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z'];
            return lowerAlpha[number - 1] || number.toString();
        }
        case 'lower-roman': {
            const romanNumerals = ['i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x', 'xi', 'xii', 'xiii', 'xiv', 'xv', 'xvi', 'xvii', 'xviii', 'xix', 'xx', 'xxi', 'xxii', 'xxiii', 'xxiv', 'xxv', 'xxvi', 'xxvii', 'xxviii', 'xxix', 'xxx', 'xxxi', 'xxxii', 'xxxiii', 'xxxiv', 'xxxv', 'xxxvi', 'xxxvii', 'xxxviii', 'xxxix', 'xl', 'xli', 'xlii', 'xliii', 'xliv', 'xlv', 'xlvi', 'xlvii', 'xlviii', 'xlix', 'l'];
            return romanNumerals[number - 1] || number.toString();
        }
        case 'upper-roman': {
            const romanNumerals = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII', 'XIII', 'XIV', 'XV', 'XVI', 'XVII', 'XVIII', 'XIX', 'XX', 'XXI', 'XXII', 'XXIII', 'XXIV', 'XXV', 'XXVI', 'XXVII', 'XXVIII', 'XXIX', 'XXX', 'XXXI', 'XXXII', 'XXXIII', 'XXXIV', 'XXXV', 'XXXVI', 'XXXVII', 'XXXVIII', 'XXXIX', 'XL', 'XLI', 'XLII', 'XLIII', 'XLIV', 'XLV', 'XLVI', 'XLVII', 'XLVIII', 'XLIX', 'L'];
            return romanNumerals[number - 1] || number.toString();
        }
        case 'number-in-circle': {
            const numberInCircle = ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨', '⑩', '⑪', '⑫', '⑬', '⑭', '⑮', '⑯', '⑰', '⑱', '⑲', '⑳'];
            return numberInCircle[number - 1] || number.toString();
        }
        case 'chosung': {
            const chosung = ['ㄱ', 'ㄴ', 'ㄷ', 'ㄹ', 'ㅁ', 'ㅂ', 'ㅅ', 'ㅇ', 'ㅈ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ'];
            return chosung[number - 1] || number.toString();
        }
        case 'ganada': {
            const ganada = ['가', '나', '다', '라', '마', '바', '사', '아', '자', '차', '카', '타', '파', '하'];
            return ganada[number - 1] || number.toString();
        }
        case 'gb-1': {
            const gb1 = ["一", "二", "三", "四", "五", "六", "七", "八", "九", "十", "十一", "十二", "十三", "十四", "十五", "十六", "十七", "十八", "十九", "二十"];
            return gb1[number - 1] || number.toString();
        }
        case 'hangul': {
            const hangul = ["일", "이", "삼", "사", "오", "육", "칠", "팔", "구", "십", "십일", "십이", "십삼", "십사", "십오", "십육", "십칠", "십팔", "십구", "이십"];
            return hangul[number - 1] || number.toString();
        }
        case 'number': {
            const numberList = ["One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen", "Twenty"];
            return numberList[number - 1] || number.toString();
        }

        default:
            return number.toString();
    }
}

// suffix 값이 space, tab인 경우에 대한 처리
function getSuffixLabel(suffix: string): string {
    if (suffix === 'space') {
        return ' ';
    }
    if (suffix === 'tab') {
        return '\t';
    }
    return '';
}

// 원래 docx의 경우 numberingList 와 paragraph가 별도의 indent를 가질 수 있지만 aspose를 통한 해석 뒤에는 paragraph의 indent가 null이 될 수 없다라는 문제 때문에 내부의 paragraph의 indent 값을 넣기 위한 작업을 위한 함수
function updateParagraphIndentation(tr, numberingListPos, storage) {
    const numberingListNode = tr.doc.nodeAt(numberingListPos);
    if (!numberingListNode) return;

    const numId = numberingListNode.attrs.numId;
    const level = numberingListNode.attrs.level || 0;

    // `abstractNumbering`에서 해당 numId의 레벨 정보 가져오기
    const abstractNumbering = storage.abstractNumberings.find(n => n.id === storage.numberings.find(n => n.numId === numId)?.abstractNumId) || BasicMultiLevelList;
    if (!abstractNumbering || !abstractNumbering.level[level]) return;

    const levelData = abstractNumbering.level[level];
    const indent = levelData.indentLeft || '0pt';
    const firstLineIndent = levelData.indentHanging ? `-${levelData.indentHanging}` : '0pt';

    // `numberingList` 내부의 첫 번째 `paragraph` 찾기
    numberingListNode.descendants((childNode, childPos) => {
        if (childNode.type.name === 'paragraph') {
            const paragraphPos = numberingListPos + childPos + 1; // 절대 위치 계산
            tr.setNodeMarkup(paragraphPos, childNode.type, {
                ...childNode.attrs,
                indent,
                firstLineIndent,
            });
            return false; // 첫 번째 `paragraph`만 수정
        }
        return true;
    });
}

// 리스트 번호 계산하여 Decoration을 만드는 함수
function calculateListDecorations(
    doc: ProseMirrorNode,
    storage: { numberings: Array<{ numId: string; abstractNumId: string }>; abstractNumberings: any[] },
): DecorationSet {
    const listConfig: Record<string, typeof BasicMultiLevelList> = {};
    const counters: Record<string, number[]> = {};
    storage.numberings.map(num => {
        const abstractNumbering = storage.abstractNumberings.find(abstract => abstract.id === num.abstractNumId);
        if (abstractNumbering) {
            listConfig[num.numId] = cloneDeep(abstractNumbering);
            counters[num.numId] = Array(abstractNumbering.level.length).fill(0);
        }
    });

    const decorations: Decoration[] = [];

    doc.descendants((node, pos) => {
        if (node.type.name === 'numberingList') {
            const level: number = node.attrs.level || 0;
            const selectedAbstractNumbering = listConfig[node.attrs.numId] || BasicMultiLevelList;
            if (!counters[node.attrs.numId]) {
                counters[node.attrs.numId] = Array(selectedAbstractNumbering.level.length).fill(0);
            }
            counters[node.attrs.numId][level] += 1;
            // 카운터가 바뀐 경우 하위 레벨의 카운터 초기화
            for (let i = level + 1; i < counters[node.attrs.numId].length; i++) {
                counters[node.attrs.numId][i] = 0;
            }

            // 리스트 번호 포맷 처리 (현재 레벨까지의 모든 숫자 포함)
            const labelFormat: string = selectedAbstractNumbering.level[level]?.text || '{L0}.';
            const listLabel: string = labelFormat.replace(/\{L\d+\}/g, match => {
                const index: number = parseInt(match.replace(/\{L(\d+)\}/, '$1'), 10);
                return formatNumber((counters[node.attrs.numId][index] || 1) + selectedAbstractNumbering.level[level]?.start - 1, selectedAbstractNumbering.level[index]?.format || 'decimal');
            });
            const suffix: string = selectedAbstractNumbering.level[level]?.suff || 'tab';

            // numberingList 내부의 첫 번째 paragraph 찾기
            let firstParagraphPos: number | null = null;
            let firstParagraphNode: ProseMirrorNode | null = null;
            let firstParagraphSize: number = 0;

            node.descendants((childNode, childPos) => {
                if (childNode.type.name === 'paragraph' && firstParagraphPos === null) {
                    firstParagraphPos = pos + 1 + childPos; // paragraph의 시작 위치
                    firstParagraphNode = childNode;
                    firstParagraphSize = childNode.nodeSize; // paragraph의 크기
                }
            });

            if (firstParagraphPos !== null && firstParagraphNode !== null) {
                const paragraphNode = firstParagraphNode as ProseMirrorNode;
                const levelFont = selectedAbstractNumbering.level[level]?.font as unknown as
                    | {
                          fontFamily?: string;
                          color?: string;
                          fontSize?: string;
                          backgroundColor?: string;
                          bold?: boolean;
                          italic?: boolean;
                          underline?: boolean;
                          strike?: boolean;
                          subscript?: boolean;
                          superscript?: boolean;
                      }
                    | undefined;
                const indentLeft: string = selectedAbstractNumbering.level[level]?.indentLeft || '0pt';
                const indentHanging: string = selectedAbstractNumbering.level[level]?.indentHanging || '0pt';
                const marginLeft: string = paragraphNode.attrs.indent || indentLeft;
                const textIndent: string = paragraphNode.attrs.firstLineIndent || `-${indentHanging}` || '0pt';

                // paragraph의 스타일을 적용하는 Decoration
                const paragraphDeco: Decoration = Decoration.node(firstParagraphPos, firstParagraphPos + firstParagraphSize, {
                    style: `margin-left: ${marginLeft}; text-indent: 0;`,
                });
                decorations.push(paragraphDeco);

                // 리스트에 스타일을 적용하는 Decoration
                const numberlingListDeco: Decoration = Decoration.node(pos, pos + node.nodeSize, {
                    style: `position: relative;`,
                });
                decorations.push(numberlingListDeco);

                // 리스트 번호를 추가하는 Decoration
                const listNumberDeco: Decoration = Decoration.widget(
                    firstParagraphPos + 1,
                    () => {
                        const wrapperSpan = document.createElement('span');
                        const span = document.createElement('span');
                        wrapperSpan.style.display = 'inline-block';
                        wrapperSpan.style.marginLeft = textIndent;
                        wrapperSpan.style.minWidth = textIndent.replace('-', '');
                        wrapperSpan.style.textIndent = '0';
                        wrapperSpan.className = 'list-number';
                        wrapperSpan.appendChild(span);
                        wrapperSpan.appendChild(document.createTextNode(getSuffixLabel(suffix)));
                        span.textContent = listLabel;
                        span.style.textAlign = selectedAbstractNumbering.level[level]?.justification;
                        wrapperSpan.style.userSelect = 'none';
                        wrapperSpan.style.whiteSpace = 'pre';
                        span.style.fontFamily = levelFont?.fontFamily || '';
                        if (span.style.fontFamily === 'Symbol' && listLabel === '') {
                            // mac 에서는 Symbol 폰트가 없고 라이선스 문제가 있어서 bullet 폰트에 한해서 대신 사용
                            span.textContent = '•';
                            span.style.fontFamily = 'Verdana,sans-serif';
                        }
                        span.style.color = levelFont?.color || '';
                        span.style.fontSize = levelFont?.fontSize || '';
                        span.style.backgroundColor = levelFont?.backgroundColor || '';
                        span.style.fontWeight = levelFont?.bold ? 'bold' : '';
                        span.style.fontStyle = levelFont?.italic ? 'italic' : '';
                        span.style.textDecoration = levelFont?.underline ? 'underline' : '';
                        span.style.textDecoration = levelFont?.strike ? 'line-through' : '';
                        span.style.verticalAlign = levelFont?.subscript ? 'sub' : '';
                        span.style.verticalAlign = levelFont?.superscript ? 'super' : '';

                        // 하위 paragraph 의 attrs.paragraphBreakFont 적용
                        wrapperSpan.style.fontSize = paragraphNode.attrs.paragraphBreakFont?.fontSize || '12pt';

                        if (!selectedAbstractNumbering.level[level]?.font && paragraphNode.attrs.paragraphBreakFont) {
                            const paragraphBreakFont = paragraphNode.attrs.paragraphBreakFont;
                            if (!span.style.fontFamily && paragraphBreakFont?.fontFamily) span.style.fontFamily = paragraphBreakFont.fontFamily;
                            if (!span.style.color && paragraphBreakFont?.color) span.style.color = paragraphBreakFont.color;
                            if (!span.style.fontSize && paragraphBreakFont?.fontSize) span.style.fontSize = paragraphBreakFont.fontSize;
                            if (!span.style.backgroundColor && paragraphBreakFont?.backgroundColor) span.style.backgroundColor = paragraphBreakFont.backgroundColor;
                            if (!span.style.fontWeight && paragraphBreakFont?.bold) span.style.fontWeight = 'bold';
                            if (!span.style.fontStyle && paragraphBreakFont?.italic) span.style.fontStyle = 'italic';
                            if (!span.style.textDecoration && paragraphBreakFont?.underline) span.style.textDecoration = 'underline';
                            if (!span.style.textDecoration && paragraphBreakFont?.strike) span.style.textDecoration = 'line-through';
                            if (!span.style.verticalAlign && paragraphBreakFont?.subscript) span.style.verticalAlign = 'sub';
                            if (!span.style.verticalAlign && paragraphBreakFont?.superscript) span.style.verticalAlign = 'super';
                        }
                        return wrapperSpan;
                    },
                    { side: -1 },
                );

                decorations.push(listNumberDeco);
            }
            return false;
        }
    });

    return DecorationSet.create(doc, decorations);
}

// Tiptap Node 확장
export const WordNumberingList = Node.create({
    name: 'numberingList',
    group: 'block list',
    content: 'paragraph',
    defining: true,

    // prettier-ignore
    addStorage() {
        return {
            numberings: [],
            abstractNumberings: [],
        };
    },

    addAttributes() {
        return {
            level: {
                default: 0,
                parseHTML: element => parseInt(element.getAttribute('data-level') || '0'),
                renderHTML: attributes => ({ 'data-level': attributes.level }),
            },
            numId: {
                default: '0',
                parseHTML: element => element.getAttribute('data-num-id') || '0',
                renderHTML: attributes => ({ 'data-num-id': attributes.numId }),
            },
        };
    },

    parseHTML() {
        return [{ tag: 'div.numbering-list' }];
    },

    renderHTML({ node }) {
        return ['div', { class: 'numbering-list list-none block', 'data-level': node.attrs.level, 'data-num-id': node.attrs.numId }, 0];
    },

    addProseMirrorPlugins() {
        const { storage } = this;
        return [
            new Plugin({
                state: {
                    init(_, { doc }) {
                        // doc와 storact를 이용하여 리스트 번호 계산
                        return calculateListDecorations(doc, storage);
                    },
                    apply(tr, oldDecorations) {
                        const setNumberingDataMeta = tr.getMeta('setNumberingData');
                        if (tr.docChanged || setNumberingDataMeta) {
                            return calculateListDecorations(tr.doc, storage);
                        }
                        return oldDecorations;
                    },
                },
                props: {
                    decorations(state) {
                        return this.getState(state);
                    },
                },
            }),
        ];
    },

    addCommands() {
        return {
            toggleOrderedList:
                (): Command =>
                ({ tr, state, dispatch }) => {
                    const { from, to } = state.selection;
                    const { storage } = this;
                    let hasChanged = false;

                    let isFirstParagraphIsOrderedList = false;
                    let firstNumberingListNode: ProseMirrorNode | null = null;
                    const paragraphs: { node: ProseMirrorNode; pos: number }[] = [];

                    // 선택된 모든 `paragraph`를 찾고, 첫 번째가 `numberingList`인지 확인
                    state.doc.nodesBetween(from, to, (node, pos) => {
                        if (paragraphs.length === 0 && node.type.name === 'numberingList') {
                            const abstractNumbering = storage.abstractNumberings.find(n => n.id === storage.numberings.find(n => n.numId === node.attrs.numId)?.abstractNumId) || BasicMultiLevelList;
                            isFirstParagraphIsOrderedList = abstractNumbering.level[0].format !== 'bullet';
                            firstNumberingListNode = node;
                        }
                        if (node.type.name === 'paragraph' || node.type.name === 'numberingList') {
                            paragraphs.push({ pos, node });
                            return false;
                        }
                    });

                    const isSameAllNumberingList = paragraphs.every(p => p.node.type.name === 'numberingList' && p.node.attrs.numId === firstNumberingListNode?.attrs.numId);

                    if (isSameAllNumberingList && isFirstParagraphIsOrderedList) {
                        // `numberingList` 해제해야 하는 경우 (모든 문단이 리스트 내부)
                        for (let i = paragraphs.length - 1; i >= 0; i--) {
                            const { pos, node } = paragraphs[i];
                            const range = state.doc.resolve(pos + 2).blockRange();

                            if (range) {
                                const target = liftTarget(range);
                                if (target !== null) {
                                    tr.setNodeAttribute(pos + 1, 'indent', '0pt');
                                    tr.setNodeAttribute(pos + 1, 'firstLineIndent', '0pt');
                                    tr.lift(range, target);
                                    hasChanged = true;
                                }
                            }
                        }
                    } else {
                        let newNumId = crypto.randomUUID();
                        if (isFirstParagraphIsOrderedList) {
                            // `numberingList` 를 첫번째 numberingList의 numId로 (첫 문단이 리스트 내부)
                            newNumId = firstNumberingListNode!.attrs.numId;
                        } else {
                            const newAbstractNumId = crypto.randomUUID();

                            storage.numberings.push({
                                numId: newNumId,
                                abstractNumId: newAbstractNumId,
                            });

                            storage.abstractNumberings.push({
                                id: newAbstractNumId,
                                ...BasicMultiLevelList,
                            });
                        }
                        for (let i = paragraphs.length - 1; i >= 0; i--) {
                            if (paragraphs[i].node.type.name === 'numberingList') {
                                tr.setNodeMarkup(paragraphs[i].pos, paragraphs[i].node.type, {
                                    ...paragraphs[i].node.attrs,
                                    numId: newNumId,
                                });
                                updateParagraphIndentation(tr, paragraphs[i].pos, storage);
                                hasChanged = true;
                            } else {
                                const range = state.doc.resolve(paragraphs[i].pos + 1).blockRange();
                                if (range) {
                                    tr.wrap(range, [{ type: this.type, attrs: { numId: newNumId, level: 0 } }]);
                                    updateParagraphIndentation(tr, range.start, storage);
                                    hasChanged = true;
                                }
                            }
                        }
                    }

                    if (hasChanged) {
                        dispatch?.(tr);
                    }

                    return hasChanged;
                },

            toggleBulletList:
                (): Command =>
                ({ tr, state, dispatch }) => {
                    const { from, to } = state.selection;
                    const { storage } = this;
                    let hasChanged = false;

                    let isFirstParagraphIsBulletList = false;
                    let firstNumberingListNode: ProseMirrorNode | null = null;
                    const paragraphs: { node: ProseMirrorNode; pos: number }[] = [];

                    // 선택된 모든 `paragraph`를 찾고, 첫 번째가 `numberingList`인지 확인
                    state.doc.nodesBetween(from, to, (node, pos) => {
                        if (paragraphs.length === 0 && node.type.name === 'numberingList') {
                            const abstractNumbering = storage.abstractNumberings.find(n => n.id === storage.numberings.find(n => n.numId === node.attrs.numId)?.abstractNumId) || BasicBulletList;
                            isFirstParagraphIsBulletList = abstractNumbering.level[0].format === 'bullet';
                            firstNumberingListNode = node;
                        }
                        if (node.type.name === 'paragraph' || node.type.name === 'numberingList') {
                            paragraphs.push({ pos, node });
                            return false;
                        }
                    });

                    const isSameAllNumberingList = paragraphs.every(p => p.node.type.name === 'numberingList' && p.node.attrs.numId === firstNumberingListNode?.attrs.numId);

                    if (isSameAllNumberingList && isFirstParagraphIsBulletList) {
                        // `numberingList` 해제해야 하는 경우 (모든 문단이 리스트 내부)
                        for (let i = paragraphs.length - 1; i >= 0; i--) {
                            const { pos, node } = paragraphs[i];
                            const range = state.doc.resolve(pos + 2).blockRange();

                            if (range) {
                                const target = liftTarget(range);
                                if (target !== null) {
                                    tr.setNodeAttribute(pos + 1, 'indent', '0pt');
                                    tr.setNodeAttribute(pos + 1, 'firstLineIndent', '0pt');
                                    tr.lift(range, target);
                                    hasChanged = true;
                                }
                            }
                        }
                    } else {
                        let newNumId = crypto.randomUUID();
                        if (isFirstParagraphIsBulletList) {
                            // `numberingList` 를 첫 번째 numberingList의 numId로 (첫 문단이 리스트 내부)
                            newNumId = firstNumberingListNode!.attrs.numId;
                        } else {
                            const newAbstractNumId = crypto.randomUUID();

                            storage.numberings.push({
                                numId: newNumId,
                                abstractNumId: newAbstractNumId,
                            });

                            storage.abstractNumberings.push({
                                id: newAbstractNumId,
                                ...BasicBulletList,
                            });
                        }
                        for (let i = paragraphs.length - 1; i >= 0; i--) {
                            if (paragraphs[i].node.type.name === 'numberingList') {
                                tr.setNodeMarkup(paragraphs[i].pos, paragraphs[i].node.type, {
                                    ...paragraphs[i].node.attrs,
                                    numId: newNumId,
                                });
                                updateParagraphIndentation(tr, paragraphs[i].pos, storage);
                                hasChanged = true;
                            } else {
                                const range = state.doc.resolve(paragraphs[i].pos + 1).blockRange();
                                if (range) {
                                    tr.wrap(range, [{ type: this.type, attrs: { numId: newNumId, level: 0 } }]);
                                    updateParagraphIndentation(tr, range.start, storage);
                                    hasChanged = true;
                                }
                            }
                        }
                    }

                    if (hasChanged) {
                        dispatch?.(tr);
                    }

                    return hasChanged;
                },

            splitNumberingList:
                (): Command =>
                ({ tr, editor, chain }) => {
                    const { selection } = editor.state;
                    const { from, to, empty } = selection;

                    if (empty) {
                        const paragraphBreakFont = getActiveParagraphBreakFont(editor);
                        return chain().focus().splitListItem(this.name).setParagraphBreakFont(paragraphBreakFont).applyParagraphBreakFont().run();
                    } else {
                        tr.delete(from, to);
                        return true;
                    }
                },

            sinkNumberingList:
                (): Command =>
                ({ tr, state, dispatch, commands }) => {
                    const { selection } = state;
                    const { from, to } = selection;
                    const { storage } = this;

                    // `from`과 `to` 사이에 있는 모든 numberingList 노드 찾기
                    state.doc.nodesBetween(from, to, (node, pos) => {
                        if (node.type.name === 'numberingList') {
                            const currentLevel = node.attrs.level || 0;
                            const numId = node.attrs.numId;
                            const maxLevel =
                                storage.abstractNumberings.find(n => n.id === storage.numberings.find(n => n.numId === numId)?.abstractNumId)?.level.length - 1 || BasicMultiLevelList.level.length - 1;

                            if (currentLevel < maxLevel) {
                                tr.setNodeMarkup(pos, node.type, {
                                    ...node.attrs,
                                    level: currentLevel + 1,
                                });

                                // ✅ indent 업데이트 (내부 paragraph 적용)
                                updateParagraphIndentation(tr, pos, storage);
                            }
                        }
                    });

                    if (tr.steps.length > 0) {
                        dispatch?.(tr);
                        if (commands.applyParagraphBreakFont) commands.applyParagraphBreakFont();
                    }
                    return tr.steps.length > 0;
                },

            // ✅ `liftNumberingList` (내어쓰기 - Shift+Tab)
            liftNumberingList:
                (): Command =>
                ({ tr, state, dispatch, commands }) => {
                    const { selection } = state;
                    const { from, to } = selection;
                    const { storage } = this;

                    // `from`과 `to` 사이에 있는 모든 numberingList 노드 찾기
                    state.doc.nodesBetween(from, to, (node, pos) => {
                        if (node.type.name === 'numberingList') {
                            const currentLevel = node.attrs.level || 0;

                            if (currentLevel > 0) {
                                tr.setNodeMarkup(pos, node.type, {
                                    ...node.attrs,
                                    level: currentLevel - 1,
                                });

                                // ✅ indent 업데이트 (내부 paragraph 적용)
                                updateParagraphIndentation(tr, pos, storage);
                            }
                        }
                    });

                    if (tr.steps.length > 0) {
                        dispatch?.(tr);
                        if (commands.applyParagraphBreakFont) commands.applyParagraphBreakFont();
                    }
                    return tr.steps.length > 0;
                },
            // Backspace 시 numberingList만 제거하고 paragraph 유지
            removeNumberingListOnBackspace:
                (): Command =>
                ({ state, tr, dispatch }) => {
                    const { selection } = state;
                    const { $from, from, to } = selection;

                    // 선택된 범위가 있으면 동작하지 않음
                    if (from !== to) {
                        return false;
                    }

                    // 현재 노드가 numberingList 내부의 paragraph인지 확인
                    if ($from.node().type.name === 'paragraph' && $from.depth > 1) {
                        const parent = $from.node($from.depth - 1);

                        if (parent.type.name === 'numberingList') {
                            // numberingList의 위치 계산
                            const numberingListPos = $from.before($from.depth - 1);

                            // numberingList 내부의 paragraph를 유지하면서 numberingList를 해제
                            const range = state.doc.resolve(numberingListPos + 1).blockRange();
                            if (range) {
                                const target = liftTarget(range);
                                if (target !== null) {
                                    tr.lift(range, target);

                                    if (dispatch) {
                                        dispatch(tr);
                                    }
                                    return true;
                                }
                            }
                        }
                    }
                    return false;
                },
            // 넘버링 화면 출력 포맷을 위한 numbering 데이터를 설정
            setNumberingData:
                ({ numberings, abstractNumberings }: { numberings: { numId: string; abstractNumId: string }[]; abstractNumberings: any[] }): Command =>
                ({ editor }) => {
                    const { storage } = this;

                    // numbering과 abstractNumbering 데이터를 외부 값으로 설정
                    storage.numberings = numberings;
                    storage.abstractNumberings = abstractNumberings;

                    // 저장된 numbering 데이터가 유효한지 확인
                    const validNumIds = numberings.map(n => n.numId);
                    const validAbstractIds = abstractNumberings.map(a => a.id);

                    storage.numberings = storage.numberings.filter(num => validNumIds.includes(num.numId));
                    storage.abstractNumberings = storage.abstractNumberings.filter(abstract => validAbstractIds.includes(abstract.id));

                    // meta 정보를 세팅하여 decoration을 다시 그리도록 함
                    editor.view.dispatch(editor.view.state.tr.setMeta('setNumberingData', true));

                    return true;
                },
        };
    },

    addKeyboardShortcuts() {
        return {
            Enter: () => this.editor.commands.splitNumberingList(),
            Tab: () => this.editor.commands.sinkNumberingList(),
            'Shift-Tab': () => this.editor.commands.liftNumberingList(),
            'Mod-Shift-7': () => this.editor.commands.toggleOrderedList(),
            Backspace: () => this.editor.commands.removeNumberingListOnBackspace(),
        };
    },
});
